import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Wallet, Plus, Star, Trash2, MapPin, Home, Briefcase, TrendingUp, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";

const PRESETS = [10, 25, 50, 100];

export default function WalletPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [wallet, setWallet] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [busy, setBusy] = useState(false);
  const [amt, setAmt] = useState(25);
  const [open, setOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);

  const load = async () => {
    const [w, f] = await Promise.all([
      api.get("/wallet").catch(() => ({ data: null })),
      api.get("/favorites").catch(() => ({ data: [] })),
    ]);
    setWallet(w.data);
    setFavorites(f.data);
  };

  useEffect(() => { load(); }, []);

  // PayPal top-up return handler. The frontend gets pushed back to
  // /wallet?paypal_topup_order_id=… when the buyer approves on PayPal.
  // We capture immediately; the endpoint is idempotent so a refresh is safe.
  useEffect(() => {
    const ppId = params.get("paypal_topup_order_id");
    const cancelled = params.get("paypal_topup_cancelled");
    if (cancelled) {
      toast.info(t("wallet.topupCancelled", "Top-up cancelled."));
      window.history.replaceState({}, "", "/wallet");
      return;
    }
    if (!ppId) return;
    setCapturing(true);
    (async () => {
      try {
        const { data } = await api.post(`/wallet/topup-paypal/capture/${ppId}`);
        if (data.status === "paid" || data.status === "already_captured") {
          toast.success(t("wallet.topupSuccess", "Top-up complete!"));
          await load();
        } else {
          toast.error(t("wallet.topupPending", "Top-up not confirmed yet."));
        }
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setCapturing(false);
        window.history.replaceState({}, "", "/wallet");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const topup = async () => {
    if (amt < 5) { toast.error(t("wallet.min", "Minimum top-up is $5")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/wallet/topup-paypal/create", {
        amount: Number(amt),
        origin_url: window.location.origin,
      });
      window.location.href = data.approve_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  const addFavorite = async (label, address) => {
    try { await api.post("/favorites", { label, address }); load(); setOpen(false); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-[600px] md:max-w-[900px] mx-auto px-4 pt-5 pb-6" data-testid="wallet-page">
      <h1 className="text-[32px] md:text-[36px] font-black tracking-tight leading-none">
        {t("wallet.title", "Wallet")}
      </h1>
      <div className="text-[13px] text-white/50 mt-1.5">
        {t("wallet.subtitle", "Top up in USD via PayPal — pay for rides faster.")}
      </div>

      {/* Balance card — quiet, monochrome, single accent */}
      <div
        className="mt-6 rounded-3xl bg-white/[0.04] border border-white/10 p-6 relative overflow-hidden"
        data-testid="wallet-balance"
      >
        <div className="flex items-center gap-2 text-white/60">
          <Wallet size={14} />
          <span className="text-[10px] uppercase font-semibold tracking-widest">
            {t("wallet.balance", "Balance")}
          </span>
        </div>
        <div className="text-5xl font-black mt-2 tracking-tight tabular-nums">
          ${(wallet?.balance ?? 0).toFixed(2)}
        </div>
        <div className="mt-3 flex items-center gap-3 text-[12px] text-white/60">
          <span className="inline-flex items-center gap-1.5">
            <Star size={12} className="text-brand-warn" fill="currentColor" />
            <span className="text-white font-bold tabular-nums">
              {wallet?.points ?? 0}
            </span>
            <span>{t("wallet.points", "points")}</span>
          </span>
          <span className="text-white/25">·</span>
          <span>{t("wallet.pointsRate", "1 pt per $1")}</span>
        </div>
      </div>

      {capturing && (
        <div
          className="mt-4 rounded-2xl bg-brand-accent/10 border border-brand-accent/25 px-4 py-3 flex items-center gap-2 text-[13px] text-brand-accent"
          data-testid="topup-capturing-banner"
        >
          <Loader2 size={14} className="animate-spin" />
          {t("wallet.topupProcessing", "Confirming your top-up with PayPal…")}
        </div>
      )}

      {/* Top-up */}
      <section
        className="mt-6 rounded-3xl bg-white/[0.04] border border-white/10 p-5"
        data-testid="topup-section"
      >
        <h2 className="text-[15px] font-bold">{t("wallet.topupTitle", "Top up")}</h2>
        <p className="text-[12px] text-white/50 mt-1">
          {t("wallet.topupSub",
             "Pay with PayPal, or with a Visa · Mastercard · Amex through PayPal's checkout (no PayPal account required).")}
        </p>

        <div className="grid grid-cols-4 gap-2 mt-4">
          {PRESETS.map((v) => (
            <button
              key={v}
              onClick={() => setAmt(v)}
              className={`rounded-2xl py-2.5 text-[14px] font-bold border transition-colors ${
                amt === v
                  ? "border-white bg-white text-black"
                  : "border-white/10 bg-white/[0.04] text-white/70 hover:bg-white/[0.08]"
              }`}
              data-testid={`topup-${v}`}
            >
              ${v}
            </button>
          ))}
        </div>

        <div className="mt-3 flex gap-2">
          <input
            type="number"
            min="5"
            max="5000"
            value={amt}
            onChange={(e) => setAmt(Number(e.target.value))}
            className="flex-1 bg-black/40 border border-white/10 rounded-2xl px-4 py-3 text-[14px] outline-none focus:border-white/40"
            data-testid="topup-amount"
          />
          <button
            onClick={topup}
            disabled={busy || capturing}
            className="pill-btn bg-white text-black rounded-full px-5 py-3 text-[13px] font-bold inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="topup-btn"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <TrendingUp size={14} />}
            {busy
              ? t("wallet.topupOpening", "Opening PayPal…")
              : t("wallet.topup", "Top up with PayPal")}
          </button>
        </div>
        <div className="mt-2 text-[11px] text-white/40">
          {t("wallet.rewardsHint", "Earn 1 loyalty point per $1 topped up.")}
        </div>
      </section>

      {/* Favorites */}
      <section className="mt-6" data-testid="favorites-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[15px] font-bold">
            {t("wallet.favorites", "Saved places")}
          </h2>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button
                className="pill-btn bg-white text-black rounded-full px-3.5 py-1.5 text-[12px] font-bold inline-flex items-center gap-1.5"
                data-testid="add-favorite-btn"
              >
                <Plus size={12} /> {t("wallet.addFav", "Add place")}
              </button>
            </DialogTrigger>
            <DialogContent className="bg-ink-surface border border-white/10 text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="text-xl font-black">
                  {t("wallet.addFav", "Add place")}
                </DialogTitle>
              </DialogHeader>
              <FavForm onSave={addFavorite} />
            </DialogContent>
          </Dialog>
        </div>
        {favorites.length === 0 ? (
          <div
            className="rounded-2xl bg-white/[0.03] border border-white/10 p-8 text-center text-[13px] text-white/50"
            data-testid="favorites-empty"
          >
            {t("wallet.noFavs", "No saved places yet.")}
          </div>
        ) : (
          <div
            className="rounded-2xl bg-white/[0.03] border border-white/10 divide-y divide-white/[0.06]"
            data-testid="favorites-list"
          >
            {favorites.map((f) => (
              <div
                key={f.favorite_id}
                className="p-3 flex items-center gap-3"
                data-testid={`fav-${f.favorite_id}`}
              >
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                  {f.label.toLowerCase().includes("home") ? <Home size={16} /> :
                   f.label.toLowerCase().includes("work") ? <Briefcase size={16} /> :
                   <MapPin size={16} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[14px]">{f.label}</div>
                  <div className="text-[12px] text-white/50 truncate">{f.address}</div>
                </div>
                <button
                  onClick={async () => { await api.delete(`/favorites/${f.favorite_id}`); load(); }}
                  className="p-2 rounded-full text-red-400 hover:bg-red-500/10"
                  data-testid={`del-fav-${f.favorite_id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Transactions */}
      {wallet?.transactions?.length > 0 && (
        <section className="mt-6" data-testid="tx-list">
          <h2 className="text-[15px] font-bold mb-3">
            {t("wallet.recent", "Recent activity")}
          </h2>
          <div className="rounded-2xl bg-white/[0.03] border border-white/10 divide-y divide-white/[0.06]">
            {wallet.transactions.map((tx, i) => (
              <div key={i} className="p-3 flex items-center justify-between">
                <div>
                  <div className="text-[13px] font-semibold">
                    {tx.type === "topup" ? t("wallet.topupLabel", "PayPal top-up") : tx.type}
                  </div>
                  <div className="text-[11px] text-white/40 mt-0.5">
                    {new Date(tx.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="text-end">
                  <div className="text-brand-accent font-black text-[15px] tabular-nums">
                    +${tx.amount?.toFixed(2)}
                  </div>
                  {tx.points > 0 && (
                    <div className="text-[10px] text-brand-warn font-semibold">
                      +{tx.points} pts
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function FavForm({ onSave }) {
  const { t } = useTranslation();
  const [label, setLabel] = useState("Home");
  const [address, setAddress] = useState("");
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        {["Home", "Work", "Other"].map((l) => (
          <button
            key={l}
            type="button"
            onClick={() => setLabel(l)}
            className={`rounded-2xl py-2 text-xs font-bold border transition-colors ${
              label === l
                ? "border-white bg-white text-black"
                : "border-white/10 bg-white/[0.04] text-white/70"
            }`}
          >
            {t(`wallet.${l.toLowerCase()}`, l)}
          </button>
        ))}
      </div>
      {label === "Other" && (
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={t("wallet.customLabel", "Label")}
          className="w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-2.5 text-sm outline-none focus:border-white/40"
        />
      )}
      <input
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        placeholder={t("wallet.addressPlaceholder", "Address")}
        className="w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-2.5 text-sm outline-none focus:border-white/40"
        data-testid="fav-address"
      />
      <button
        onClick={() => address && onSave(label, address)}
        className="pill-btn w-full bg-white text-black rounded-full py-3 font-bold"
        data-testid="fav-save"
      >
        {t("wallet.save", "Save place")}
      </button>
    </div>
  );
}
